import { Component, Inject, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  FileInfo,
  FileUploadError,
  GALAXY_UPLOADER_SERVICE_TOKEN,
  GalaxyUploaderModule,
} from '@vendasta/galaxy/uploader';
import { ImageUploadService } from '../image-upload.service';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'ai-image-upload-modal',
  imports: [MatDialogModule, GalaxyUploaderModule, TranslateModule, GalaxyAvatarModule, MatButtonModule, MatIconModule],
  templateUrl: './image-upload-modal.component.html',
  styleUrl: './image-upload-modal.component.scss',
  providers: [{ provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: ImageUploadService }],
})
export class ImageUploadModalComponent {
  file = signal<FileInfo | null>(null);
  private readonly uploadService = inject(GALAXY_UPLOADER_SERVICE_TOKEN) as ImageUploadService;
  protected readonly state = signal('change');

  private readonly dialogRef = inject(MatDialogRef<ImageUploadModalComponent>);

  private readonly snack = inject(SnackbarService);

  constructor(@Inject(MAT_DIALOG_DATA) public data: { imageURL: string; defaultSvg: string }) {
    this.state.set(data.imageURL ? 'view' : 'change');
  }

  onRemove(): void {
    this.dialogRef.close({ removed: true });
  }

  onError(event: FileUploadError): void {
    this.snack.openErrorSnack(event.error.message);
    this.dialogRef.close();
  }

  onUpload(fileInfo: FileInfo | null): void {
    if (!fileInfo) return;
    this.uploadService.clear();
    this.dialogRef.close({ newImageUrl: fileInfo.url });
  }

  onFilesChanged(files: FileInfo[]): void {
    const fileInfo = files?.[0];
    if (fileInfo) {
      this.file.set(fileInfo);
    }
  }

  async endEdit(blob?: Blob | null): Promise<void> {
    const currentFile = this.file();
    if (blob && currentFile) {
      this.uploadService.setCroppedImage(currentFile, blob);
      this.uploadService.uploadQueuedFiles();

      firstValueFrom(this.uploadService.fileUploaded$).then((fileInfo) => {
        this.dialogRef.close({
          newImageUrl: fileInfo.url,
        });
      });
    } else {
      this.uploadService.clear();
      this.file.set(null);
      this.dialogRef.close();
    }
  }
}
